import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import {useMachine} from '@xstate/react';
import React, {useCallback, useEffect} from 'react';

import {distinctDomainToKey} from 'antibodies/AntibodyUtils';
import AntibodyNestedDataCreationModalMachine from 'antibodies/nested-data-creation/AntibodyNestedDataCreationModalMachine';
import {areConstantDomainsFullyDefined} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  AntibodyCreationStepKey,
  ANTIBODY_CREATION_STEPS,
  TranslationParameters,
  ConstantAaDomainEntity,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import FinalizePane from 'antibodies/nested-data-creation/panes/FinalizePane';
import GetDomainsPane from 'antibodies/nested-data-creation/panes/GetDomainsPane';
import SelectFormatPane from 'antibodies/nested-data-creation/panes/SelectFormatPane';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant, StepNavSize, StepItemStatus} from 'ui-library/StepNav';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

interface NestedCreateAndRegisterAntibodiesModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  initialFolderId?: string | null;
}

const useStyles = createStyles((theme) => ({
  stepNav: {
    marginBottom: '16px',
  },
}));

export default function NestedCreateAndRegisterAntibodiesModal({
  onRequestClose,
  onRequestSubmit,
  initialFolderId,
}: NestedCreateAndRegisterAntibodiesModalProps): JSX.Element {
  const {classes} = useStyles();
  const [ndcModalState, send] = useMachine(AntibodyNestedDataCreationModalMachine, {
    input: {initialFolderId},
  });

  useEffect(() => {
    if (ndcModalState.matches('done')) {
      onRequestSubmit();
    }
  }, [ndcModalState, onRequestSubmit]);

  const getCurrentStep = (): AntibodyCreationStepKey => {
    if (ndcModalState.matches('selectFormat')) {
      return AntibodyCreationStepKey.SELECT_FORMAT;
    }
    if (ndcModalState.matches('defineDomains')) {
      return AntibodyCreationStepKey.DEFINE_DOMAINS;
    }
    if (
      ndcModalState.matches('finalize') ||
      ndcModalState.matches('saving') ||
      ndcModalState.matches('done')
    ) {
      return AntibodyCreationStepKey.FINALIZE;
    }
    return AntibodyCreationStepKey.SELECT_FORMAT;
  };

  const currentStep = getCurrentStep();
  const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
  const statusByStep: {[key in AntibodyCreationStepKey]: StepItemStatus | null} = Object.fromEntries(
    ANTIBODY_CREATION_STEPS.map((step, index) => [
      step.key,
      index <= currentStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    ])
  ) as {[key in AntibodyCreationStepKey]: StepItemStatus | null};

  function handleSubmitButton(): void {
    const currentStepIndex = ANTIBODY_CREATION_STEPS.findIndex((step) => step.key === currentStep);
    if (currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1) {
      send({type: 'createAndRegister'});
    } else {
      send({type: 'next'});
    }
  }

  const handlePrevious = (): void => {
    send({type: 'back'});
  };

  function getButtons(): Array<ModalButton> {
    const buttons: Array<ModalButton> = [
      {
        text: 'Cancel',
        onClick: onRequestClose,
        cancel: true,
        pullLeft: true,
      },
    ];
    if (currentStepIndex !== 0) {
      buttons.push({
        text: 'Back',
        onClick: handlePrevious,
      });
    }
    if (currentStepIndex === ANTIBODY_CREATION_STEPS.length - 1) {
      buttons.push({
        text: 'Create and register',
        submit: true,
        disabled: ndcModalState.context.outputConfig.antibodies.folderId == null,
      });
    } else {
      let isNextDisabled = false;
      if (currentStepIndex === 0) {
        isNextDisabled =
          ndcModalState.context.selectedFormatId == null ||
          ndcModalState.context.selectedRegistryId == null ||
          ndcModalState.context.inputConfig.variableDnaDomains.pairSchemaId == null ||
          ndcModalState.context.inputConfig.constantAaDomains.schemaId == null;
      } else if (currentStepIndex === 1) {
        isNextDisabled =
          ndcModalState.context.inputConfig.variableDnaDomains.pairIds.length === 0 ||
          !areConstantDomainsFullyDefined(
            Object.values(ndcModalState.context.inputConfig.constantAaDomains.entities)
          );
      }
      buttons.push({
        text: 'Next',
        submit: true,
        disabled: isNextDisabled,
      });
    }
    return buttons;
  }

  const setSelectedRegistryIdAndDependentConfig = useCallback(
    (registryId: string | null, pairSchemaId: string | null, constantDomainSchemaId: string | null) => {
      send({
        type: 'selectRegistryAndDependentConfig',
        registryId,
        pairSchemaId,
        constantDomainSchemaId,
      });
    },
    [send]
  );

  return (
    <Modal
      title="Create antibodies"
      buttons={getButtons()}
      onRequestSubmit={handleSubmitButton}
      onRequestClose={onRequestClose}
      scrollable={true}
      expandHeight={true}
      large={true}
    >
      <div className={classes.stepNav}>
        <StepNav
          steps={ANTIBODY_CREATION_STEPS}
          statusByStep={statusByStep}
          currentStepKey={currentStep}
          onUpdateCurrentStep={undefined}
          variant={StepNavVariant.STACKED}
          size={StepNavSize.FULL_WIDTH}
        />
      </div>
      <div {...getTestProps({component: 'NestedCreateAndRegisterAntibodiesModal', element: 'step-content'})}>
        {currentStep === AntibodyCreationStepKey.SELECT_FORMAT && (
          <SelectFormatPane
            selectedRegistryId={ndcModalState.context.selectedRegistryId}
            setSelectedRegistryIdAndDependentConfig={setSelectedRegistryIdAndDependentConfig}
            selectedFormatId={ndcModalState.context.selectedFormatId}
            setSelectedFormat={(format: AntibodyFormatGraphQL | null) => {
              send({type: 'selectFormat', format});
            }}
            startingDataType={ndcModalState.context.startingDataType}
            outputTypes={ndcModalState.context.outputTypes}
          />
        )}
        {currentStep === AntibodyCreationStepKey.DEFINE_DOMAINS && (
          <GetDomainsPane
            pairSchemaId={ndcModalState.context.inputConfig.variableDnaDomains.pairSchemaId!}
            constantDomainSchemaId={ndcModalState.context.inputConfig.constantAaDomains.schemaId!}
            selectedPairIds={ndcModalState.context.inputConfig.variableDnaDomains.pairIds}
            setSelectedPairIds={(selectedPairIds: Array<string>) => {
              send({type: 'selectDnaDomainPairs', pairIds: selectedPairIds});
            }}
            aaDomainTranslationParameters={
              ndcModalState.context.outputConfig.variableAaDomains.translationParameters
            }
            updateAaDomainTranslationParameters={(
              translationParametersUpdate: Partial<TranslationParameters>
            ) => {
              send({
                type: 'setTranslationParameters',
                translationParameters: {
                  ...ndcModalState.context.outputConfig.variableAaDomains.translationParameters,
                  ...translationParametersUpdate,
                },
              });
            }}
            constantAaDomainEntities={ndcModalState.context.inputConfig.constantAaDomains.entities}
            selectConstantDomainEntity={(constantDomainEntity: ConstantAaDomainEntity) => {
              send({
                type: 'selectConstantDomains',
                constantDomainEntities: {
                  ...ndcModalState.context.inputConfig.constantAaDomains.entities,
                  [distinctDomainToKey(constantDomainEntity)]: constantDomainEntity,
                },
              });
            }}
            registryId={ndcModalState.context.selectedRegistryId!}
          />
        )}
        {currentStep === AntibodyCreationStepKey.FINALIZE && (
          <FinalizePane
            folderId={ndcModalState.context.outputConfig.antibodies.folderId || null}
            numPairs={ndcModalState.context.inputConfig.variableDnaDomains.pairIds.length}
            setProjectFolder={(folderId: string) => {
              send({type: 'setProjectFolder', folderId});
            }}
            formatId={ndcModalState.context.selectedFormatId!}
          />
        )}
      </div>
    </Modal>
  );
}
